import React, { forwardRef, useEffect, useState } from 'react'
import { useCombobox, UseComboboxPropGetters } from 'downshift'
import { nanoid } from 'nanoid'
import mixpanel from 'mixpanel-browser'

import usePlacesAutocomplete, { Suggestion, getDetails } from 'use-places-autocomplete'

import { TextInput, TextInputProps } from '../TextInput'
import { InputGroup } from '../InputGroup'
import { InputRightElement } from '../InputElement'
import { Icon } from '../Icon'
import { Box, BoxProps } from '../Box'
import { Text } from '../Text'
import { Spinner } from '../Spinner'
import { Portal } from '../Portal'
import { useDimensions } from '../../hooks/useDimensions'
import { usePrevious } from '../../hooks/usePrevious'
import { theme } from '../../theme'

const GOOGLE_MAPS_LIBRARIES = ['places']

const AddressInputMenuWrapper = (props: BoxProps) => (
  <Box
    position="fixed"
    shadow="lg"
    maxWidth="500px"
    zIndex={theme.zIndices.dropdown}
    bg="white"
    border="2px"
    borderColor="gray.300"
    mt={1}
    {...props}
  />
)

/////////////////////////////////////////////////////////////////////////////////////////////////

interface AddressInputMenu extends BoxProps {
  isOpen: boolean
  searchResults: Suggestion[]
  highlightedIndex: number
  getMenuProps: UseComboboxPropGetters<any>['getMenuProps']
  getItemProps: UseComboboxPropGetters<any>['getItemProps']
  isLoading: boolean
  searchValue: string
}

const AddressInputMenu = ({
  isOpen,
  searchResults,
  minWidth,
  top,
  left,
  highlightedIndex,
  getMenuProps,
  getItemProps,
  isLoading,
  searchValue,
}: AddressInputMenu) => (
  <Portal>
    <Box as="ul" {...getMenuProps()} zIndex={99999}>
      {isOpen &&
        (searchResults.length > 0 ? (
          <AddressInputMenuWrapper minWidth={minWidth} top={top} left={left}>
            {searchResults.map((item, index) => (
              <Box
                as="li"
                bg={highlightedIndex === index ? 'green.500' : null}
                key={`${item?.place_id}`}
                px={3}
                py={2}
                cursor="pointer"
                listStyleType="none"
                borderBottom="1px"
                borderColor="gray.200"
                {...getItemProps({ item, index })}
              >
                <Text color={highlightedIndex === index ? 'white' : 'black'} fontWeight="medium">
                  {item?.description}
                </Text>
              </Box>
            ))}
          </AddressInputMenuWrapper>
        ) : isLoading ? null : searchValue.length === 0 ? null : (
          <AddressInputMenuWrapper minWidth={minWidth} top={top} left={left} py={4} px={3}>
            <Text>Sorry, no results!</Text>
          </AddressInputMenuWrapper>
        ))}
    </Box>
  </Portal>
)

/////////////////////////////////////////////////////////////////////////////////////////////////

export type AddressObject = {
  address_line1: string
  address_line2: string
  postcode: string
  city: string
  state: string
  lat: number
  lng: number
}

type ThenArg<T> = T extends PromiseLike<infer U> ? U : T
type AddressDetails = ThenArg<ReturnType<typeof getDetails>>

export const processAddress = (value: AddressDetails): AddressObject => {
  if (typeof value === 'string') return

  const reducedAddress = value.address_components.reduce<{ [key: string]: string }>(
    (prev, curr) => {
      return { ...prev, [curr.types[0]]: curr.short_name }
    },
    {},
  )

  const prefix = reducedAddress.subPremise || reducedAddress.subpremise || reducedAddress.floor

  const address_line1 = `${prefix ? `${prefix} / ` : ''}${reducedAddress.street_number} ${
    reducedAddress.route
  }`.trim()

  const address_line2 = ''

  const postcode = reducedAddress.postal_code
  const city = reducedAddress.locality
  const state = reducedAddress.administrative_area_level_1

  return {
    address_line1,
    address_line2,
    postcode,
    city,
    state,
    lat: value.geometry.location.lat(),
    lng: value.geometry.location.lng(),
  }
}

export interface AddressInputProps extends Omit<TextInputProps, 'onChangeValue'> {
  defaultValue?: any // TODO: determine what the default value should be of this input
  onChangeValue?: (value: AddressObject | string) => void
  onChangeInputValue?: (value: string) => void
  doesReturnStringOnChangeValue?: boolean
  transformResult?: (result: AddressObject) => any
  townsOnly?: boolean
  source?: string
  apiKey?: string
}

export const AddressInput = forwardRef(
  (
    {
      defaultValue,
      onChangeValue,
      apiKey,
      townsOnly,
      onChangeInputValue,
      doesReturnStringOnChangeValue,
      transformResult,
      size,
      source,
      onFocus,
      onBlur,
      ...restProps
    }: AddressInputProps,
    ref,
  ) => {
    const {
      ready: isReady,
      value,
      suggestions: { status, data, loading: isLoading },
      setValue,
    } = usePlacesAutocomplete({
      debounce: 300,
      requestOptions: {
        componentRestrictions: { country: 'au' },
        types: [townsOnly ? '(cities)' : 'address'],
      },
    })

    const previousDefaultValue = usePrevious(defaultValue)

    useEffect(() => {
      if (defaultValue && defaultValue != previousDefaultValue) {
        setValue(defaultValue)
      }
    }, [defaultValue])

    const [anchorRef, dimensions] = useDimensions()

    /* type=route excludes street address. See "221/28 Little Lonsdale Street".
     * Only do this if we need to obtain an address object in onChangeValue. */
    const searchResults = doesReturnStringOnChangeValue
      ? data
      : data?.filter(item => !item?.types?.includes('route'))

    const comboBox = useCombobox<Suggestion>({
      items: searchResults,
      initialInputValue: restProps.value as string,
      // selectedItem: defaultValue,
      onSelectedItemChange: async ({ selectedItem }) => {
        const addressString = selectedItem.description

        try {
          mixpanel.track('Address Search', {
            address: addressString,
            source,
            townsOnly: townsOnly ? 'Yes' : 'No',
          })
        } catch (e) {}

        // TODO: make call here to create address in database,
        // use ID of created address in onChangeValue
        const details = await getDetails(selectedItem)

        const processedDetails = processAddress(details)

        const value = doesReturnStringOnChangeValue
          ? addressString
          : transformResult?.(processedDetails) || processedDetails

        onChangeValue && onChangeValue(value)
      },
      itemToString: item => (item ? item.description : ''),
      onInputValueChange: ({ inputValue }) => {
        const onlyDigitsRegex = new RegExp('^[0-9]*$')

        /* Check to see if the input text is numbers only (ie: Postcode),
         * Only make a google api request if the result is false (ie: anything other than a postcode ) */
        if (!onlyDigitsRegex.test(inputValue)) {
          setValue(inputValue.trim())
        }

        onChangeInputValue && onChangeInputValue(inputValue.trim())
        onChangeValue && onChangeValue(null)
      },
      onIsOpenChange: ({ isOpen }) => {
        // Sometimes when the menu opens, it is nowhere near the input. This fixes it
        isOpen && window.dispatchEvent(new Event('resize'))
      },
    })

    /* There's an age old issue with browsers autocompleting fields with `autocomplete="off"` set.
     * If you generate a random string and set `autocomplete` to that, it should fix it. */
    const randomAutoCompleteString = nanoid()

    return (
      <Box ref={anchorRef}>
        <InputGroup size={size} {...comboBox.getComboboxProps()}>
          <TextInput
            placeholder={townsOnly ? 'e.g. Melbourne' : 'e.g. 42 Wallaby Way, Sydney'}
            {...restProps}
            {...comboBox.getInputProps({ onFocus, onBlur, ref: ref as any })}
            autoComplete="off"
          />

          <InputRightElement>
            {isLoading ? (
              <Spinner size="sm" color="green.500" />
            ) : (
              <Icon name="place" color="gray.400" fontSize="1.2rem" />
            )}
          </InputRightElement>
        </InputGroup>

        <AddressInputMenu
          isOpen={comboBox.isOpen && status === 'OK'}
          searchResults={searchResults}
          searchValue={value}
          isLoading={isLoading}
          minWidth={`${dimensions.width}px`}
          top={`${dimensions.y + dimensions.height}px`}
          left={`${dimensions.x}px`}
          getMenuProps={comboBox.getMenuProps}
          getItemProps={comboBox.getItemProps}
          highlightedIndex={comboBox.highlightedIndex}
        />
      </Box>
    )
  },
)

export default AddressInput
